import { useSearchParams } from '@shared/hooks';
import { StatusLabel } from '@shared/components';
import SearchResultsContent from '../components/SearchResultsContent';
import { useEffect, useState } from 'react';
import { TSearchItem } from '../types';
import { TFolder, TItem, TResolvedItemNames, useFolder } from '@modules/items';
import { useSearch } from '../api';
import { calculateRowsPerPage } from '@shared/helpers';
import { UI_MARGIN_TOP } from '../constants';

export type TResolvedData = {
  total: number;
  items: (TSearchItem | TItem)[];
  resolvedNames?: TResolvedItemNames;
};

const SearchResults = () => {
  const { searchParams, change } = useSearchParams();
  const query = searchParams.get('query');
  const page = Number(searchParams.get('page'));
  const pageSize = Number(searchParams.get('pageSize'));
  const folderId = searchParams.get('folderId') || undefined;

  useEffect(() => {
    if (Number(page) < 1) change('page', '1');
    if (Number(pageSize) < 1) change('pageSize', calculateRowsPerPage(UI_MARGIN_TOP));
  }, [page, pageSize]);

  const {
    data: searchData,
    error: searchError,
    isLoading: isLoadingSearch,
  } = useSearch({
    query,
    page,
    pageSize,
    folderId,
  });
  const { data: folder, error: folderError, isLoading: isLoadingFolder } = useFolder();

  const [data, setData] = useState<TResolvedData | undefined>();
  useEffect(() => {
    if (folderError || searchError) {
      console.error('Error fetching search data:', folderError || searchError); // dev log
      setData(undefined); // clear data on error
      return;
    }
    /**
     * Avoid setting data if:
     * - No folder data is available (should not happen)
     * - Folder data is still loading
     * - Search data is being loaded
     */
    if (!folder || isLoadingFolder || (!searchData && isLoadingSearch)) return;
    // Use either search data (if available) or folder data recent items (should always be available)
    setData({
      total: searchData ? searchData.total : folder.recentItems.length,
      items: searchData ? searchData.items : folder.recentItems.slice((page - 1) * pageSize, page * pageSize),
      resolvedNames: searchData ? searchData?.resolvedNames : (folder as TFolder).resolvedNames,
    });
  }, [searchData, isLoadingSearch, isLoadingFolder, folder, page, pageSize]);

  return (
    <StatusLabel
      data={data}
      error={folderError || searchError}
      loadingMessage="Loading search results..."
      errorMessage="Loading search results failed!"
      render={(resolvedData: TResolvedData) => (
        <SearchResultsContent
          items={resolvedData.items}
          total={resolvedData.total}
          resolvedNames={resolvedData.resolvedNames}
        />
      )}
    />
  );
};

export default SearchResults;
